import { z } from 'zod'

import {
  ReactionGroupSchema,
  ThreadReactionSummarySchema,
  ThreadUserSchema,
} from '../thread'
import { ThreadCommentVisibilityEnum } from '../thread-comment-visibility'
import { ReactionGroup, ThreadReactionSummary } from '../thread-reaction'
import { User } from '../user'

export { ThreadCommentVisibilityEnum } from '../thread-comment-visibility'

// Thread Comment sort field enum
export enum ThreadCommentSortFieldEnum {
  CREATED_AT = 'createdAt',
  SEQUENCE = 'sequence',
}

export enum ThreadCommentHistoryAction {
  EDIT = 'EDIT',
  DELETE = 'DELETE',
}

/**
 * Interface representing a ThreadComment entity
 */
export interface ThreadComment {
  id: string
  _eventId?: string
  content: string
  createdAt: Date
  updatedAt?: Date
  editedAt?: Date | null
  deletedAt?: Date | null
  threadId: string
  organizationId: string
  authorId: string
  /** Server-stamped identity; absent on historical and human comments. */
  agentAccountId?: string | null
  agentAccountName?: string | null
  parentCommentId?: string | null
  clientMessageId?: string | null
  sequence: number
  // Optional: legacy persisted client objects lack it; undefined means INTERNAL
  visibility?: ThreadCommentVisibilityEnum
  // True when the author has client portal access for this organization.
  isClientAuthor?: boolean
  /** Note comments: id of the `commentAnchor` mark this root comment is pinned to. */
  anchorId?: string | null
  /** Snapshot of the highlighted text when the anchored comment was created. */
  anchorText?: string | null
  /** Root comments only: when the conversation was resolved (null = open). */
  resolvedAt?: Date | string | null
  resolvedById?: string | null
  resolvedBy?: Partial<User> | null

  // Related entities
  author: Partial<User>

  // Additional computed properties
  userReaction: ThreadReactionSummary | null
  reactionGroups?: ReactionGroup[] // Slack-style grouped reactions
  totalReactions: number
  totalReplies: number
  attachmentCount: number
  hasAttachments: boolean
}

export interface ThreadCommentHistory {
  id: string
  commentId: string
  threadId: string
  editorId: string
  action: ThreadCommentHistoryAction
  previousContent?: string | null
  newContent?: string | null
  createdAt: Date
  editor?: Partial<User>
}

export interface ThreadCommentChangesMeta {
  lastCommentSequence: number
  hasMore: boolean
  serverTime: Date
}

export interface ThreadCommentChangesResponse {
  data: ThreadComment[]
  meta: ThreadCommentChangesMeta
}

/** Client-generated anchor ids: a uuid or similar url-safe token. */
export const THREAD_COMMENT_ANCHOR_ID_PATTERN = /^[A-Za-z0-9_-]{8,64}$/
/** Quoted text snapshots are truncated to this length before sending. */
export const THREAD_COMMENT_ANCHOR_TEXT_MAX_LENGTH = 500

// Create Thread Comment schema
export const CreateThreadCommentSchema = z.object({
  threadId: z.string().uuid(),
  parentCommentId: z.string().uuid().nullable(),
  content: z.string(),
  clientMessageId: z.string().uuid().nullable().optional(),
  // Only honored for root comments; replies inherit their parent's visibility
  visibility: z.nativeEnum(ThreadCommentVisibilityEnum).optional(),
  // Inline anchor (note comment threads only, root comments only). Both or neither.
  anchorId: z
    .string()
    .regex(THREAD_COMMENT_ANCHOR_ID_PATTERN)
    .nullable()
    .optional(),
  anchorText: z
    .string()
    .max(THREAD_COMMENT_ANCHOR_TEXT_MAX_LENGTH)
    .nullable()
    .optional(),
})

export type CreateThreadComment = z.infer<typeof CreateThreadCommentSchema>

export const UpdateThreadCommentVisibilitySchema = z.object({
  visibility: z.nativeEnum(ThreadCommentVisibilityEnum),
})

export type UpdateThreadCommentVisibility = z.infer<
  typeof UpdateThreadCommentVisibilitySchema
>

export const UpdateThreadCommentResolutionSchema = z.object({
  resolved: z.boolean(),
})

export type UpdateThreadCommentResolution = z.infer<
  typeof UpdateThreadCommentResolutionSchema
>
